import { CellProps } from 'react-table'
import { AtendimentoList } from '../../../../atendimento/core/_models'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { PlainHeader, sortableColumn, StackedCell, TableColumn } from '../../../../../../components'
import { AtendimentoEstado } from '../../../../../../../_metronic/helpers'
import { TFunction } from '_metronic/i18n/Metronici18n'

/**
 * Atraso entre o agendamento e o início (finalizado) ou agora (aguardando), em "1h 20min".
 * Demais estados, ou sem atraso, ficam sem valor (traço). `solicitacao`/`inicio` são
 * LocalDateTime (com hora), então `new Date` é seguro aqui.
 */
const tempoAtraso = ({ solicitacao, estado, inicio }: AtendimentoList): string | undefined => {
  if (!solicitacao) return undefined

  const isFinalizado = estado?.id === AtendimentoEstado.Finalizado
  const isAguardando = estado?.id === AtendimentoEstado.Aguardando
  if (!isAguardando && !isFinalizado) return undefined

  const agendado = new Date(solicitacao)
  const referencia = isFinalizado && inicio ? new Date(inicio) : new Date()
  if (referencia <= agendado) return undefined

  const totalMin = Math.floor((referencia.getTime() - agendado.getTime()) / 60000)
  const horas = Math.floor(totalMin / 60)
  const minutos = totalMin % 60

  if (horas === 0) return `${minutos}min`
  return minutos > 0 ? `${horas}h ${minutos}min` : `${horas}h`
}

export const monitoramentoColumns = (t: TFunction): ReadonlyArray<TableColumn<AtendimentoList>> => [
  // Paciente com o protocolo como apoio; os dois seguem ordenáveis.
  sortableColumn<AtendimentoList>({
    id: 'paciente',
    useQueryRequest,
    fields: [
      { field: 'paciente', label: t('cols.paciente') },
      { field: 'protocolo', label: t('cols.protocolo') },
    ],
    Cell: ({ row }: CellProps<AtendimentoList>) => (
      <StackedCell primary={row.original.paciente?.nome} secondary={row.original.protocolo} />
    ),
  }),
  // Tipo de atendimento com a especialidade como apoio.
  sortableColumn<AtendimentoList>({
    id: 'tipo',
    cellWidth: '200px',
    hideOnMobile: true,
    useQueryRequest,
    fields: [
      { field: 'tipo', label: t('cols.tipo') },
      { field: 'especialidade', label: t('cols.especialidade') },
    ],
    Cell: ({ row }: CellProps<AtendimentoList>) => (
      <StackedCell primary={row.original.tipo} secondary={row.original.especialidade?.nome} />
    ),
  }),
  sortableColumn<AtendimentoList>({
    id: 'estado.nome',
    cellWidth: '180px',
    useQueryRequest,
    fields: [{ field: 'estado.nome', label: t('cols.status') }],
    Cell: ({ row }: CellProps<AtendimentoList>) => (
      <StackedCell primary={row.original.estado?.nome} />
    ),
  }),
  // Agendamento (ordem padrão: mais recente primeiro) com o profissional como apoio; o
  // profissional não é ordenável no backend.
  sortableColumn<AtendimentoList>({
    id: 'solicitacao',
    cellWidth: '220px',
    hideOnMobile: true,
    useQueryRequest,
    fields: [
      { field: 'solicitacao', label: t('cols.agendamento') },
      { field: 'responsavel.nome', label: 'Profissional', sortable: false },
    ],
    Cell: ({ row }: CellProps<AtendimentoList>) => (
      <StackedCell
        tabular
        primary={row.original.solicitacaoFormatada}
        secondary={row.original.responsavel?.nome}
      />
    ),
  }),
  {
    Header: () => <PlainHeader>Tempo de atraso</PlainHeader>,
    id: 'tempoAtraso',
    cellWidth: '136px',
    Cell: ({ row }: CellProps<AtendimentoList>) => (
      <StackedCell tabular primary={tempoAtraso(row.original)} />
    ),
  },
]
